import { getUUID } from './fingerprint'
import { APP_NAME } from '@/config/constant'

import config from '@/config'

// 获取操作系统信息
const getOSInfo = () => {
    const userAgent = navigator.userAgent.toLowerCase()

    if (userAgent.includes('iphone') || userAgent.includes('ipad') || userAgent.includes('ipod')) {
        const match = userAgent.match(/os (\d+)_(\d+)_?(\d+)?/)
        const version = match ? `${match[1]}.${match[2]}${match[3] ? '.' + match[3] : ''}` : 'unknown'
        return { osType: 'ios', osVersion: version, deviceBrand: 'apple' }
    } else if (userAgent.includes('android')) {
        const match = userAgent.match(/android (\d+\.?\d*\.?\d*)/)
        const version = match ? match[1] : 'unknown'
        return { osType: 'android', osVersion: version, deviceBrand: 'android' }
    } else if (userAgent.includes('windows')) {
        return { osType: 'windows', osVersion: 'unknown', deviceBrand: 'pc' }
    } else if (userAgent.includes('mac')) {
        return { osType: 'macos', osVersion: 'unknown', deviceBrand: 'apple' }
    } else if (userAgent.includes('linux')) {
        return { osType: 'linux', osVersion: 'unknown', deviceBrand: 'pc' }
    }

    return { osType: 'unknown', osVersion: 'unknown', deviceBrand: 'unknown' }
}

// 获取设备型号
const getDeviceModel = () => {
    const userAgent = navigator.userAgent

    // iPhone 型号检测
    if (userAgent.includes('iPhone')) {
        if (userAgent.includes('iPhone15')) return 'iPhone 15'
        if (userAgent.includes('iPhone14')) return 'iPhone 14'
        if (userAgent.includes('iPhone13')) return 'iPhone 13'
        if (userAgent.includes('iPhone12')) return 'iPhone 12'
        if (userAgent.includes('iPhone11')) return 'iPhone 11'
        return 'iPhone'
    }

    // Android 设备型号检测
    const androidMatch = userAgent.match(/\(([^)]+)\)/)
    if (androidMatch && userAgent.includes('Android')) {
        const deviceInfo = androidMatch[1]
        const modelMatch = deviceInfo.match(/([^;]+)$/)
        if (modelMatch) {
            return modelMatch[1].trim()
        }
    }

    // 桌面设备
    if (userAgent.includes('Windows')) return 'Windows PC'
    if (userAgent.includes('Macintosh')) return 'Mac'
    if (userAgent.includes('Linux')) return 'Linux PC'

    return 'Unknown Device'
}

// 获取网络类型
const getNetworkType = () => {
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection
    if (connection) {
        return connection.effectiveType || connection.type || 'unknown'
    }
    return 'unknown'
}

// 获取电池信息
const getBatteryInfo = async () => {
    try {
        const battery = await (navigator as any).getBattery?.()
        if (battery) {
            return {
                level: Math.round(battery.level * 100),
                charging: battery.charging
            }
        }
    } catch (error) {
        console.warn('Battery API not supported')
    }
    return { level: 100, charging: false }
}

// 获取内存信息
const getMemoryInfo = () => {
    const memory = (performance as any).memory
    if (memory) {
        return {
            totalMemory: memory.totalJSHeapSize?.toString() || '0',
            availableMemory: (memory.totalJSHeapSize - memory.usedJSHeapSize)?.toString() || '0'
        }
    }
    return {
        totalMemory: '0',
        availableMemory: '0'
    }
}

// 获取时区信息
const getTimezoneInfo = () => {
    const offset = new Date().getTimezoneOffset()
    const offsetHours = Math.abs(offset / 60)
    const offsetSign = offset <= 0 ? '+' : '-'

    return {
        timezone: `UTC${offsetSign}${offsetHours}`,
        currentTimeZone: `GMT${offsetSign}${offsetHours}`
    }
}

export const getDeviceInfo = async () => {
    const osInfo = getOSInfo()
    const deviceModel = getDeviceModel()
    const networkType = getNetworkType()
    const batteryInfo = await getBatteryInfo()
    const memoryInfo = getMemoryInfo()
    const timezoneInfo = getTimezoneInfo()
    const deviceCode = await getUUID()

    // 获取屏幕信息
    const screenWidth = window.screen.width
    const screenHeight = window.screen.height
    const screenDensity = window.devicePixelRatio || 1
    const windowWidth = window.innerWidth
    const windowHeight = window.innerHeight

    // 获取语言信息
    const deviceLanguage = navigator.language || 'en'

    // 获取应用版本（从环境变量或配置中获取）
    const appVersion = config.version

    // 检测是否为模拟器（简单检测）
    const isSimulator = screenWidth === 375 && screenHeight === 667 && window.devicePixelRatio === 2

    return {
        deviceCode: deviceCode,
        deviceModel: deviceModel,
        deviceBrand: osInfo.deviceBrand,
        osType: osInfo.osType,
        osVersion: osInfo.osVersion,
        screenWidth: screenWidth,
        screenHeight: screenHeight,
        screenDensity: screenDensity,
        screenScale: screenDensity,
        deviceLanguage: deviceLanguage,
        appVersion: appVersion,
        deviceTimezone: timezoneInfo.timezone,
        pluginSystemInfo: {
            totalMemory: memoryInfo.totalMemory,
            deviceIPAddresses: 'unknown', // 浏览器无法直接获取IP地址
            simCardNumInPhone: 1, // 浏览器无法获取SIM卡信息
            checkIsSimulator: isSimulator,
            currentTimeZone: timezoneInfo.currentTimeZone,
            networkType: networkType,
            deviceRealName: deviceModel,
            batteryValue: batteryInfo.level,
            availableMemory: memoryInfo.availableMemory,
            systemVersion: osInfo.osVersion,
            isJailBreak: false, // 浏览器无法检测越狱
            deviceIPAdressForWifi: 'unknown', // 浏览器无法直接获取IP地址
            deviceName: deviceModel,
            appVersion: appVersion,
            deviceLanguageUsed: deviceLanguage,
            unusedDisk: 0, // 浏览器无法获取磁盘信息
            totalDisk: 0 // 浏览器无法获取磁盘信息
        },
        uniSystemInfo: {
            SDKVersion: '',
            appId: import.meta.env.VITE_UNI_APP_ID,
            appLanguage: deviceLanguage.split('-')[0] || 'en',
            appName: APP_NAME,
            appVersion: appVersion,
            appVersionCode: '231',
            appWgtVersion: appVersion,
            brand: osInfo.deviceBrand,
            browserName: getBrowserName(),
            browserVersion: getBrowserVersion(),
            deviceBrand: osInfo.deviceBrand,
            deviceId: deviceCode,
            deviceModel: deviceModel,
            deviceOrientation: window.innerHeight > window.innerWidth ? 'portrait' : 'landscape',
            devicePixelRatio: screenDensity,
            deviceType: getDeviceType(),
            isUniAppX: false,
            language: deviceLanguage,
            model: deviceModel,
            osLanguage: deviceLanguage,
            osName: osInfo.osType,
            osTheme: getTheme(),
            osVersion: osInfo.osVersion,
            pixelRatio: screenDensity,
            platform: osInfo.osType,
            safeArea: {
                left: 0,
                right: windowWidth,
                top: getSafeAreaTop(),
                bottom: windowHeight - getSafeAreaBottom(),
                width: windowWidth,
                height: windowHeight - getSafeAreaTop() - getSafeAreaBottom()
            },
            safeAreaInsets: {
                top: getSafeAreaTop(),
                right: 0,
                bottom: getSafeAreaBottom(),
                left: 0
            },
            screenHeight: screenHeight,
            screenWidth: screenWidth,
            statusBarHeight: getSafeAreaTop(),
            system: `${osInfo.osType} ${osInfo.osVersion}`,
            theme: getTheme(),
            ua: navigator.userAgent,
            uniCompileVersion: '4.45',
            uniCompilerVersion: '4.45',
            uniPlatform: 'app',
            uniRuntimeVersion: '4.64',
            version: '1.9.9.81563',
            windowBottom: 0,
            windowHeight: windowHeight,
            windowTop: 0,
            windowWidth: windowWidth
        }
    }
}

// 辅助函数：获取浏览器名称
const getBrowserName = () => {
    const userAgent = navigator.userAgent.toLowerCase()
    if (userAgent.includes('chrome')) return 'chrome'
    if (userAgent.includes('firefox')) return 'firefox'
    if (userAgent.includes('safari')) return 'safari'
    if (userAgent.includes('edge')) return 'edge'
    if (userAgent.includes('opera')) return 'opera'
    return 'unknown'
}

// 辅助函数：获取浏览器版本
const getBrowserVersion = () => {
    const userAgent = navigator.userAgent
    const browserName = getBrowserName()

    let match
    switch (browserName) {
        case 'chrome':
            match = userAgent.match(/chrome\/(\d+\.\d+\.\d+\.\d+)/i)
            break
        case 'firefox':
            match = userAgent.match(/firefox\/(\d+\.\d+)/i)
            break
        case 'safari':
            match = userAgent.match(/version\/(\d+\.\d+)/i)
            break
        case 'edge':
            match = userAgent.match(/edge\/(\d+\.\d+)/i)
            break
        default:
            return 'unknown'
    }

    return match ? match[1] : 'unknown'
}

// 辅助函数：获取设备类型
export const getDeviceType = () => {
    const userAgent = navigator.userAgent.toLowerCase()
    if (userAgent.includes('mobile') || userAgent.includes('android') || userAgent.includes('iphone')) {
        return 'phone'
    }
    if (userAgent.includes('tablet') || userAgent.includes('ipad')) {
        return 'tablet'
    }
    return 'desktop'
}

// 辅助函数：获取主题
const getTheme = () => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark'
    }
    return 'light'
}

// 辅助函数：获取安全区域顶部
const getSafeAreaTop = () => {
    // 对于iPhone X及以上设备，状态栏高度通常为44px或47px
    const userAgent = navigator.userAgent
    if (userAgent.includes('iPhone') && window.screen.height >= 812) {
        return 47
    }
    return 20 // 默认状态栏高度
}

// 辅助函数：获取安全区域底部
const getSafeAreaBottom = () => {
    // 对于有Home指示器的设备
    const userAgent = navigator.userAgent
    if (userAgent.includes('iPhone') && window.screen.height >= 812) {
        return 34
    }
    return 0
}

// 判断是否是苹果系统（包括iOS和macOS）
export const isAppleSystem = () => {
    const userAgent = navigator.userAgent.toLowerCase()

    // 检测iOS设备（iPhone、iPad、iPod）
    const isIOS = userAgent.includes('iphone') || userAgent.includes('ipad') || userAgent.includes('ipod')

    // 检测macOS设备
    const isMacOS = userAgent.includes('mac') && !userAgent.includes('iphone')

    return isIOS || isMacOS
}

// 是否启用了系统“减少动态效果”偏好（用于关闭自动轮播/循环动画）
export const prefersReducedMotion = (): boolean =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
